import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useSnackbar } from 'notistack';
import { RouteComponentProps } from 'react-router-dom';
import catchOnly from 'utils/catchOnly';

import * as Yup from 'yup';
import {
  composeSchema,
  isPhoneNumber,
  isSsn,
  isZipCode,
} from 'utils/validations';

import FmcForm from 'components/FmcForm';
import LookupFormView from '../../LookupFormView';
import { FormValues } from '../PhoneLookup';

import useOTPSender, { OTPSenderError } from 'hooks/requests/otpSender';

import { NonClientError } from 'hooks/requests/fetchUtils';
import useNavigationHelpers from 'hooks/useNavigationHelpers';

import { mergeApplication } from 'state/creditApplication';

import useFieldCustomization from 'hooks/useFieldCustomization';

type PropTypes = RouteComponentProps & {
  onSubmit: (values: FormValues) => void;
  values: FormValues;
};

const LookupForm: React.FC<PropTypes> = props => {
  const { isLastFour } = useFieldCustomization();
  const isLastFourBool = isLastFour();
  const [sendOTP] = useOTPSender();
  const { onSubmit: onSubmitSetValues, match, values } = props;

  const { push } = useNavigationHelpers();
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const schema = useMemo(
    () =>
      Yup.object().shape({
        mobilePhone: composeSchema(
          Yup.string().label('Mobile #'),
          isPhoneNumber,
          Yup.string()
            .required()
            .default(''),
        ),

        ssn: Yup.string()
          .label(isLastFourBool ? 'Last 4 SSN' : 'SSN')
          .concat(isSsn(isLastFourBool))
          .required()
          .default(''),

        zip: composeSchema(
          Yup.string().label('ZIP Code'),
          Yup.string()
            .required()
            .default(''),
          isZipCode,
        ),
      }),
    [isLastFourBool],
  );

  const onSubmit = useCallback(
    async (rawValues: FormValues) => {
      const values = schema.cast(rawValues);

      // Merge zip for use with address typeahead feature.
      dispatch(
        mergeApplication({
          ssn: values.ssn,
          mobilePhone: values.mobilePhone,
          zip: values.zip,
        }),
      );

      try {
        await sendOTP({ phone: values.mobilePhone as string });
        onSubmitSetValues(values);
        push(`${match.path}/otp-verification`.replace('//', '/'));
      } catch (e) {
        if (catchOnly(e, NonClientError, OTPSenderError)) {
          enqueueSnackbar('Something has gone wrong. Please try again later.', {
            variant: 'error',
            persist: true,
          });
        }
      }
    },
    [
      enqueueSnackbar,
      push,
      sendOTP,
      schema,
      dispatch,
      onSubmitSetValues,
      match,
    ],
  );

  return (
    <FmcForm
      initialValues={{ ...values }}
      onSubmit={onSubmit}
      validationSchema={schema}
    >
      {formProps => <LookupFormView {...formProps} />}
    </FmcForm>
  );
};

export default LookupForm;
